'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import Link from 'next/link'

import { cn } from '@/lib/utils'
import { fetchWithAuth } from '@/lib/utils/request'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/hooks/use-auth'
import { useSidebarStore } from '@/lib/stores/sidebar-store'
import { useCreateDialogs } from '@/lib/hooks/use-create-dialogs'
import { useI18n } from '@/lib/hooks/use-i18n'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ThemeToggle } from '@/components/common/ThemeToggle'
import { Separator } from '@/components/ui/separator'
import {
  Book,
  Search,
  Bot,
  Shuffle,
  Settings,
  LogOut,
  ChevronLeft,
  Menu,
  FileText,
  Plus,
  Wrench,
  Mic,
  Command,
  User,
  Workflow,
  ArrowLeftRight,
  Images,
  Palette,
  Crown,
  Globe,
  Monitor,
  Store,
  Blocks,
  Clock,
  FolderKanban,
  Building2,
} from 'lucide-react'
import DASHBOARD_API from '@/api/DASHBOARD_API'
import { getDashboardApiUrl } from '@/lib/config'


import { toast } from 'sonner'

// ... existing imports ...

const navigation = [
  {
    titleKey: 'navigation.section.ai_employees',
    items: [
      { labelKey: 'navigation.item.digital_employees', href: '/digital-employees', icon: User, status: 'normal' },
      { labelKey: 'navigation.item.browser_employees', href: '/browser-employees', icon: Globe, status: 'dev' },
      { labelKey: 'navigation.item.agents', href: '/agents', icon: Bot, status: 'normal' },
    ],
  },
  {
    titleKey: 'navigation.section.capabilities',
    items: [
      { labelKey: 'navigation.item.projects', href: '/projects', icon: FolderKanban, status: 'dev' },
      { labelKey: 'navigation.item.marketplace', href: '/marketplace', icon: Store, status: 'normal' },
      { labelKey: 'navigation.item.workflows', href: '/workflows', icon: Workflow, status: 'normal' },
      { labelKey: 'navigation.item.cron', href: '/cron', icon: Clock, status: 'normal' },
    ],
  },
  {
    titleKey: 'navigation.section.collect',
    items: [
      { labelKey: 'navigation.item.sources', href: '/sources', icon: FileText, status: 'normal' },
      { labelKey: 'navigation.item.materials', href: '/materials', icon: Images, status: 'normal' },
    ],
  },
  {
    titleKey: 'navigation.section.process',
    items: [
      { labelKey: 'navigation.item.notebooks', href: '/notebooks', icon: Book, status: 'normal' },
      { labelKey: 'navigation.item.search', href: '/search', icon: Search, status: 'normal' },
    ],
  },
  {
    titleKey: 'navigation.section.create',
    items: [
      { labelKey: 'navigation.item.image_generator', href: '/image-generator', icon: Palette, status: 'dev' },
      { labelKey: 'navigation.item.podcasts', href: '/podcasts', icon: Mic, status: 'dev' },
    ],
  },
  {
    titleKey: 'navigation.section.manage',
    items: [
      { labelKey: 'navigation.item.organization', href: '/organization', icon: Building2, status: 'dev' },
      { labelKey: 'navigation.item.models', href: '/models', icon: Bot, status: 'normal' },
      { labelKey: 'navigation.item.transformations', href: '/transformations', icon: Shuffle, status: 'normal' },
      { labelKey: 'navigation.item.skills', href: '/skills', icon: Blocks, status: 'normal' },
      { labelKey: 'navigation.item.settings', href: '/settings', icon: Settings, status: 'normal' },
      { labelKey: 'navigation.item.advanced', href: '/advanced', icon: Wrench, status: 'normal' },
      { labelKey: 'navigation.item.member_center', href: '/member', icon: Crown, status: 'normal' },
    ],
  },
] as const



type CreateTarget = 'digital_employees' | 'source' | 'notebook'

type RemoteCustomerInfo = {
  tenantName?: string
  tenantCode?: string
  tenantRole?: string
  name?: string
  avatar?: string
  spaceCode?: string
}

const detectUserSystem = () => {
  if (typeof navigator === 'undefined') return null
  const ua = navigator.userAgent.toLowerCase()
  if (ua.includes('mac')) return 'macOS'
  if (ua.includes('win')) return 'Windows'
  if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) return 'iOS'
  if (ua.includes('android')) return 'Android'
  if (ua.includes('linux')) return 'Linux'
  return null
}

export function AppSidebar() {
  const pathname = usePathname()
  const { logout } = useAuth()
  const { t } = useI18n()
  const { isCollapsed, toggleCollapse } = useSidebarStore()
  const { openDigitalEmployeeDialog, openSourceDialog, openNotebookDialog } =
    useCreateDialogs()

  const [createMenuOpen, setCreateMenuOpen] = useState(false)
  const [isMac, setIsMac] = useState(true) // Default to Mac for SSR
  const [userSystem, setUserSystem] = useState<string>('')
  const [customerInfo, setCustomerInfo] = useState<RemoteCustomerInfo | null>(null)
  const [avatarError, setAvatarError] = useState(false)
  const displayInitial = (
    customerInfo?.name ||
    customerInfo?.tenantName ||
    userSystem ||
    t('user.systemUnknown')
  ).charAt(0)
  const tenantSummary =
    customerInfo && (customerInfo.tenantName || customerInfo.tenantCode)
      ? [customerInfo.tenantName, customerInfo.tenantCode].filter(Boolean).join(' · ')
      : t('user.tenantUnknown')
  const spaceSummary = customerInfo?.spaceCode || t('user.spaceUnknown')
  const rawRole = customerInfo?.tenantRole?.toLowerCase()
  const resolvedRoleKey = rawRole ? `user.role.${rawRole}` : ''
  const resolvedRole =
    resolvedRoleKey && t(resolvedRoleKey) !== resolvedRoleKey
      ? t(resolvedRoleKey)
      : rawRole ?? t('user.role.unknown')
  const displayName = customerInfo?.name || t('user.menuTitle')
  const tooltipLine = customerInfo ? tenantSummary : userSystem || t('user.systemUnknown')

  // Detect platform for keyboard shortcut display
  useEffect(() => {
    setIsMac(navigator.platform.toLowerCase().includes('mac'))
    const detected = detectUserSystem()
    if (detected) {
      setUserSystem(detected)
    }
  }, [])

  // Fetch remote customer/tenant info when available
  useEffect(() => {
    let cancelled = false

    const fetchCustomerInfo = async () => {
      try {
        const dashboardApiUrl = await getDashboardApiUrl()
        // Remote endpoint requires POST with Authorization header (injected by fetchWithAuth)
        const response = await fetchWithAuth(`${dashboardApiUrl}${DASHBOARD_API.auth.customerQuery}`, {
          method: 'POST',
          cache: 'no-store'
        })

        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`)
        }

        const body = await response.json()

        if (cancelled) return

        const data = body?.data as RemoteCustomerInfo | undefined
        if (!data) return

        setCustomerInfo({
          name: data.name,
          avatar: data.avatar,
          tenantName: data.tenantName,
          tenantCode: data.tenantCode,
          tenantRole: data.tenantRole,
          spaceCode: data.spaceCode,
        })
      } catch (error) {
        if (!cancelled) {
          console.warn('Failed to load remote customer info:', error)
        }
      }
    }

    fetchCustomerInfo()

    return () => {
      cancelled = true
    }
  }, [])

  const handleCreateSelection = (target: CreateTarget) => {
    setCreateMenuOpen(false)

    if (target === 'digital_employees') {
      openDigitalEmployeeDialog()
    } else if (target === 'source') {
      openSourceDialog()
    } else if (target === 'notebook') {
      openNotebookDialog()
    }
  }

  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={cn(
          'app-sidebar flex h-full min-h-0 flex-col bg-sidebar border-sidebar-border border-r transition-all duration-300',
          isCollapsed ? 'w-16' : 'w-64'
        )}
      >
        <div
          className={cn(
            'flex h-16 items-center group',
            isCollapsed ? 'justify-center px-2' : 'justify-between px-4'
          )}
        >
          {isCollapsed ? (
            <div className="relative flex items-center justify-center w-full">
              <Image
                src="/logo.svg"
                alt="即策AI"
                width={41}
                height={28}
                className="transition-opacity group-hover:opacity-0"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleCollapse}
                className="absolute text-sidebar-foreground hover:bg-sidebar-accent opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Menu className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Image src="/logo.svg" alt="即策AI" width={28} height={20} />
                <span className="text-base font-medium text-sidebar-foreground">
                  即策AI
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleCollapse}
                className="text-sidebar-foreground hover:bg-sidebar-accent"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>

        <nav
          className={cn(
            'flex-1 space-y-1 overflow-y-auto py-4',
            isCollapsed ? 'px-2' : 'px-3'
          )}
        >
          <div
            className={cn(
              'mb-4',
              isCollapsed ? 'px-0' : 'px-3'
            )}
          >
            <DropdownMenu open={createMenuOpen} onOpenChange={setCreateMenuOpen}>
              {isCollapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button
                        onClick={() => setCreateMenuOpen(true)}
                        variant="default"
                        size="sm"
                        className="w-full justify-center px-2 bg-primary hover:bg-primary/90 text-primary-foreground border-0"
                        aria-label={t('navigation.create')}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="right">{t('navigation.create.tooltip')}</TooltipContent>
                </Tooltip>
              ) : (
                <DropdownMenuTrigger asChild>
                  <Button
                    onClick={() => setCreateMenuOpen(true)}
                    variant="default"
                    size="sm"
                    className="w-full justify-start bg-primary hover:bg-primary/90 text-primary-foreground border-0"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('navigation.create')}
                  </Button>
                </DropdownMenuTrigger>
              )}

              <DropdownMenuContent
                align={isCollapsed ? 'end' : 'start'}
                side={isCollapsed ? 'right' : 'bottom'}
                className="w-48"
              >
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault()
                    handleCreateSelection('digital_employees')
                  }}
                  className="gap-2"
                >
                  <User className="h-4 w-4" />
                  {t('navigation.create.digital_employees')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault()
                    handleCreateSelection('source')
                  }}
                  className="gap-2"
                >
                  <FileText className="h-4 w-4" />
                  {t('navigation.create.source')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault()
                    handleCreateSelection('notebook')
                  }}
                  className="gap-2"
                >
                  <Book className="h-4 w-4" />
                  {t('navigation.create.notebook')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {navigation.map((section, index) => (
            <div key={section.titleKey}>
              {index > 0 && (
                <Separator className="my-3" />
              )}
              <div className="space-y-1">
                {!isCollapsed && (
                  <h3 className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/60">
                    {t(section.titleKey)}
                  </h3>
                )}

                {section.items.map((item) => {
                  const isActive = pathname.startsWith(item.href)
                  const isDev = item.status === 'dev'

                  const handleClick = (e: React.MouseEvent) => {
                    if (isDev) {
                      e.preventDefault()
                      toast.info(t('common.stay_tuned'))
                    }
                  }

                  const button = (
                    <Button
                      variant={isActive ? 'secondary' : 'ghost'}
                      className={cn(
                        'w-full gap-3 text-sidebar-foreground',
                        isActive &&
                        'bg-sidebar-accent text-sidebar-accent-foreground',
                        isCollapsed ? 'justify-center px-2' : 'justify-start',
                        isDev && 'opacity-70'
                      )}
                      asChild={!isDev}
                      onClick={isDev ? handleClick : undefined}
                    >
                      {isDev ? (
                        <div className="flex w-full items-center gap-3">
                          <item.icon className="h-4 w-4" />
                          {!isCollapsed && (
                            <div className="flex flex-1 items-center justify-between">
                              <span>{t(item.labelKey)}</span>
                              <span className="text-[10px] bg-muted px-1 rounded text-muted-foreground ml-2">DEV</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <Link href={item.href}>
                          <item.icon className="h-4 w-4" />
                          {!isCollapsed && <span>{t(item.labelKey)}</span>}
                        </Link>
                      )}
                    </Button>
                  )

                  if (isCollapsed) {
                    return (
                      <Tooltip key={item.labelKey}>
                        <TooltipTrigger asChild>{button}</TooltipTrigger>
                        <TooltipContent side="right">
                          {t(item.labelKey)} {isDev && `(${t('common.stay_tuned')})`}
                        </TooltipContent>
                      </Tooltip>
                    )
                  }

                  return (
                    <div key={item.labelKey}>
                      {button}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </nav>

        <div
          className={cn(
            'border-t border-sidebar-border p-3 space-y-2',
            isCollapsed && 'px-2'
          )}
        >
          {/* Command Palette hint */}
          {!isCollapsed && (
            <div className="px-3 py-1.5 text-xs text-sidebar-foreground/60">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <Command className="h-3 w-3" />
                  {t('controls.quickActions')}
                </span>
                <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                  {isMac ? <span className="text-xs">⌘</span> : <span>Ctrl+</span>}K
                </kbd>
              </div>
              <p className="mt-1 text-[10px] text-sidebar-foreground/40">
                {t('controls.quickActionsHint')}
              </p>
            </div>
          )}

          <div
            className={cn(
              'flex',
              isCollapsed ? 'justify-center' : 'justify-start'
            )}
          >
            {isCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <ThemeToggle iconOnly />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">{t('controls.theme')}</TooltipContent>
              </Tooltip>
            ) : (
              <ThemeToggle />
            )}
          </div>

          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className={cn(
                      'w-full justify-start gap-3 px-0 py-0 rounded-full border-0',
                      // Light theme
                      'bg-gradient-to-br from-primary/20 via-primary/10 to-primary/5',
                      'hover:bg-gradient-to-br hover:from-primary/30 hover:via-primary/20 hover:to-primary/10',
                      // Dark theme
                      'dark:bg-gradient-to-br dark:from-primary/30 dark:via-primary/20 dark:to-primary/10',
                      'dark:hover:bg-gradient-to-br dark:hover:from-primary/40 dark:hover:via-primary/30 dark:hover:to-primary/20',
                      'transition-all duration-300 ease-in-out',
                      'hover:shadow-lg hover:shadow-primary/20 dark:hover:shadow-primary/30',
                      isCollapsed && 'justify-center'
                    )}
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary uppercase overflow-hidden">
                      {customerInfo?.avatar && !avatarError ? (
                        <Image
                          src={customerInfo.avatar}
                          alt={customerInfo.name || 'User avatar'}
                          width={36}
                          height={36}
                          className="h-9 w-9 object-cover"
                          onError={(e) => {
                            console.error('[Avatar] Failed to load avatar image:', customerInfo.avatar, e)
                            setAvatarError(true)
                          }}
                        />
                      ) : (
                        displayInitial
                      )}
                    </div>
                    {!isCollapsed && (
                      <div className="flex flex-col items-start text-left">
                        <span className="text-sm font-medium text-sidebar-foreground">
                          {displayName}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {tenantSummary}
                        </span>
                      </div>
                    )}
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="right">
                {t('user.systemTooltip', { system: tooltipLine })}
              </TooltipContent>
            </Tooltip>

            <DropdownMenuContent
              align={isCollapsed ? 'end' : 'start'}
              side={isCollapsed ? 'right' : 'top'}
              className="w-60"
            >
              <DropdownMenuLabel>{t('user.menuTitle')}</DropdownMenuLabel>
              <div className="flex items-center gap-3 px-2 pb-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary uppercase overflow-hidden">
                  {customerInfo?.avatar && !avatarError ? (
                    <Image
                      src={customerInfo.avatar}
                      alt={customerInfo.name || 'User avatar'}
                      width={40}
                      height={40}
                      className="h-10 w-10 object-cover"
                      onError={(e) => {
                        console.error('[Avatar] Failed to load avatar image (dropdown):', customerInfo.avatar, e)
                        setAvatarError(true)
                      }}
                    />
                  ) : (
                    displayInitial
                  )}
                </div>
                <div className="space-y-0.5">
                  <div className="text-sm font-medium text-foreground">
                    {displayName}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {tenantSummary}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t('user.spaceLabel')}: {spaceSummary}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t('user.roleLabel')}: {resolvedRole}
                  </div>
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault()
                }}
                className="gap-2"
              >
                <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
                {t('user.switchTenant')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault()
                  logout()
                }}
                variant="destructive"
                className="gap-2"
              >
                <LogOut className="h-4 w-4" />
                {t('controls.signOut')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </TooltipProvider>
  )
}
